import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { array, bool, func } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { 
  getArtArtists,
  isArtArtistsFetchInProgress,
  getArtArtistsError,
} from '../../ducks/artistUsernames.duck';

import {
  Page,
  LayoutSingleColumn,
  H1,
  H2,
  Avatar,
  NamedLink,
  UserDisplayName,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { fetchArtists } from './ArtistDirectoryPage.duck';
import { getCroppedImageStyle, getImageUrl } from '../../util/imageUtils';

// Title/description for the <title> and meta description tags. Hardcoded (rather than
// pulled from translations) because Console-hosted translations already define
// ArtistDirectoryPage.title/description and would silently override an en.json edit here.
const SEO_TITLE = 'Meet Our Artists — Jewish Wall Art & Judaica Makers | Joodaic';
const SEO_DESCRIPTION =
  "Browse Joodaic's directory of independent Jewish artists — original wall art, home décor, Judaica, and gifts, including giclée prints, from artists in Israel and the diaspora. Every artist verified, ships worldwide.";

// Helper component for individual artist list item
const ArtistListItem = ({ artist }) => {
  const { id, username, name, publicPromoCode, whatYouSell, locationCity } = artist;
  const featuredListings = artist.featuredListings || [];
  
  // Use the properly structured user object from the server
  const userForComponents = {
    id: { uuid: id },
    type: 'user',
    name: name,
    profileImage: artist.profileImage,
  };

  const displayName = artist.name;

  return (
    <div className="px-6 py-6 bg-white border-b border-gray-100">
      {/* Artist header */}
      <div className="flex items-center gap-4 mb-4 py-1">
        <Avatar 
          className="w-12 h-12 flex-shrink-0"
          user={userForComponents}
          renderSizes="48px"
          disableProfileLink={true}
        />
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium text-gray-900 leading-tight m-0 p-0">{displayName}</h3>
          {(whatYouSell || locationCity) && (
            <p className="text-sm text-gray-600 m-0 p-0">
              {[whatYouSell, locationCity].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
        
        <NamedLink
          className="text-sm text-white font-medium px-4 py-2 bg-[var(--marketplaceColor)] rounded-full hover:bg-[var(--marketplaceColor)]/80 transition-colors whitespace-nowrap"
          name="PublicProfilePage"
          params={{ username }}
        >
          <FormattedMessage id="ArtistDirectoryPage.viewProfile" />
        </NamedLink>
      </div>
      
      {/* Image grid using featuredListings */}
      {featuredListings.length > 0 ? (
        <div className="relative">
          {/* Sale Badge */}
          {publicPromoCode && (
            <div className="absolute top-2 left-2 bg-red-600 text-white px-4 py-2 rounded text-sm font-bold z-10">
              {publicPromoCode.percentageOff}% Off
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {featuredListings.map(({ listingId, imageUrl, cropData }, index) => {
            if (!imageUrl) return null;
            
            return (
              <NamedLink 
                key={listingId}
                name="ListingPageCanonical"
                params={{ id: listingId }}
                className="block aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity border border-gray-200"
              >
                <img
                  src={getImageUrl(imageUrl)}
                  alt={`Featured listing ${index + 1}`}
                  className="w-full h-full object-cover"
                  style={getCroppedImageStyle(cropData)}
                  loading="lazy"
                />
              </NamedLink>
            );
          })}
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <FormattedMessage id="ArtistDirectoryPage.noListings" />
        </div>
      )}
    </div>
  );
};

export const ArtistDirectoryPageComponent = props => {
  const {
    scrollingDisabled,
    artists,
    fetchArtistsInProgress,
    fetchArtistsError,
  } = props;

  const pageTitle = SEO_TITLE;
  const pageDescription = SEO_DESCRIPTION;

  // Interleave art and judaica artists so both categories appear throughout the list
  const allArtists = artists || [];
  const artArtists = allArtists.filter(a => a.metadata?.artistCategory === 'art');
  const judaicaArtists = allArtists.filter(a => a.metadata?.artistCategory === 'judaica');
  const interleaved = [];
  const maxLen = Math.max(artArtists.length, judaicaArtists.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < artArtists.length) interleaved.push(artArtists[i]);
    if (i < judaicaArtists.length) interleaved.push(judaicaArtists[i]);
  }
  const displayArtists = interleaved;
  const hasArtists = displayArtists.length > 0;
  const hasError = fetchArtistsError;
  
  // Only show loading if we have no artists AND fetch is in progress
  // This prevents loading state during SSR hydration when data is already available
  const isLoading = !hasArtists && fetchArtistsInProgress;

  const content = (
    <div className="max-w-4xl mx-auto px-6 py-6 md:py-8 lg:py-12">
      <H2 className="text-gray-700 mb-4 md:mb-6 text-center">
        <FormattedMessage id="ArtistDirectoryPage.heading" />
      </H2>
      <p className="text-base text-gray-600 leading-6 mb-8 md:mb-12 text-center">
        <FormattedMessage id="ArtistDirectoryPage.description" />
      </p>

      {hasError ? (
        <div className="flex justify-center items-center min-h-[200px] text-lg text-red-600">
          <FormattedMessage id="ArtistDirectoryPage.error" />
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center min-h-[200px] text-lg text-gray-600">
          <FormattedMessage id="ArtistDirectoryPage.loading" />
        </div>
      ) : hasArtists ? (
        <div className="bg-gray-50 rounded-lg overflow-hidden">
          {displayArtists
            .filter(artist => {
              // Only show artists with featured listings
              const featuredListings = artist.featuredListings || [];
              return featuredListings.length > 0;
            })
            .map(artist => (
              <ArtistListItem 
                key={artist.id} 
                artist={artist}
              />
            ))}
        </div>
      ) : (
        <div className="flex justify-center items-center min-h-[200px] text-lg text-gray-500">
          <FormattedMessage id="ArtistDirectoryPage.noArtists" />
        </div>
      )}
    </div>
  );

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: pageDescription,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {content}
      </LayoutSingleColumn>
    </Page>
  );
};

ArtistDirectoryPageComponent.defaultProps = {
  artists: [],
  fetchArtistsError: null,
};

ArtistDirectoryPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  artists: array,
  fetchArtistsInProgress: bool.isRequired,
  fetchArtistsError: propTypes.error,
  onFetchArtists: func.isRequired,
};

const mapStateToProps = state => {
  // Use art-specific data
  const artists = getArtArtists(state);
  const fetchArtistsInProgress = isArtArtistsFetchInProgress(state);
  const fetchArtistsError = getArtArtistsError(state);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    artists,
    fetchArtistsError,
    fetchArtistsInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchArtists: () => dispatch(fetchArtists()),
});

const ArtistDirectoryPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ArtistDirectoryPageComponent);

export default ArtistDirectoryPage; 